import React from 'react';
import { cn } from '../../lib/utils';

export const LT_EGRESO_KEYS = [
  'iibb', 'expensas', 'edemsa', 'municipalidad_agua', 'impuesto_inmobiliario',
  'servicio_limpieza', 'marketing_pauta', 'puesta_punto_cowork', 'mantenimiento_general',
];

export const LT_ROWS_META = {
  ingresos_membresias: 'Ingresos por membresías',
  iibb: 'IIBB',
  expensas: 'Expensas',
  edemsa: 'Edemsa',
  municipalidad_agua: 'Municipalidad y Agua',
  impuesto_inmobiliario: 'Impuesto inmobiliario',
  servicio_limpieza: 'Servicio de limpieza',
  marketing_pauta: 'Marketing y pauta',
  puesta_punto_cowork: 'Legales/contables',
  mantenimiento_general: 'Mantenimiento general',
  gastos_extraordinarios: 'Gastos extraordinarios',
  recupero_expensas: 'Recupero expensas puestos en uso (3)',
};

const fmt = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString('es-AR', { maximumFractionDigits: 0 });
  return `${v < 0 ? '-' : ''}$${s}`;
};

// Cálculo cliente (espeja la lógica del backend) a partir de `filas` + fee %.
export function computeLT(filas, feePct) {
  const num = (k, m) => parseFloat(filas?.[k]?.[m]) || 0;
  const trip = (fn) => {
    const m1 = fn('m1'), m2 = fn('m2'), m3 = fn('m3');
    return { m1, m2, m3, trimestre: m1 + m2 + m3 };
  };
  const rowT = (k) => trip((m) => num(k, m));
  const egr = (m) => LT_EGRESO_KEYS.reduce((s, k) => s + num(k, m), 0);
  const totalEg = trip(egr);
  const resAntes = trip((m) => num('ingresos_membresias', m) - egr(m));
  const feeOf = (m) => {
    const r = num('ingresos_membresias', m) - egr(m);
    return r > 0 ? (r * (Number(feePct) || 0)) / 100 : 0;
  };
  const fee = trip(feeOf);
  const neto = trip((m) => (num('ingresos_membresias', m) - egr(m)) - num('gastos_extraordinarios', m) - feeOf(m) + num('recupero_expensas', m));
  return { totalEg, resAntes, fee, neto };
}

const COLS = ['m1', 'm2', 'm3'];

/**
 * Tabla de Liquidación Trimestral del pool Cowork.
 * Props:
 *  - filas: { key: {m1,m2,m3,obs} }
 *  - feePct: number
 *  - mesesLabels: [l1,l2,l3]
 *  - editable: bool (admin)
 *  - onCellChange(key, field, value)  // field: m1|m2|m3|obs
 *  - onMesLabelChange(idx, value)
 */
export const LiquidacionTrimestralTable = ({
  filas = {}, feePct = 8, mesesLabels = ['Mes 1', 'Mes 2', 'Mes 3'],
  editable = false, onCellChange, onMesLabelChange,
}) => {
  const filasSafe = filas && typeof filas === 'object' ? filas : {};
  const labelsSafe = Array.isArray(mesesLabels) ? mesesLabels : ['Mes 1', 'Mes 2', 'Mes 3'];
  const c = computeLT(filasSafe, feePct);
  const rowTri = (k) => COLS.reduce((s, m) => s + (parseFloat(filasSafe?.[k]?.[m]) || 0), 0);
  // Una columna está inactiva si su mes no aplica (ej. trimestre Nov-Dic no tiene Mes 3).
  const colInactive = COLS.map((_, i) => !labelsSafe[i] || String(labelsSafe[i]).trim() === '');

  const NumCell = ({ rowKey, col }) => {
    const idx = COLS.indexOf(col);
    const val = filas?.[rowKey]?.[col];
    if (colInactive[idx]) {
      return <span className="text-slate-300 text-sm">—</span>;
    }
    if (editable) {
      return (
        <input
          type="number"
          className="w-full bg-transparent text-right font-mono text-sm tabular-nums px-1 py-1 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:bg-white"
          value={val ?? ''}
          onChange={(e) => onCellChange?.(rowKey, col, e.target.value)}
          data-testid={`lt-${rowKey}-${col}`}
          placeholder="0"
        />
      );
    }
    return <span className="font-mono text-sm tabular-nums text-slate-700">{fmt(val)}</span>;
  };

  const EditRow = ({ rowKey }) => (
    <tr className="border-b border-slate-100" data-testid={`lt-row-${rowKey}`}>
      <td className="py-1.5 px-3 text-sm text-slate-700 sticky left-0 bg-white">{LT_ROWS_META[rowKey]}</td>
      {COLS.map((col) => (
        <td key={col} className="py-1 px-2 text-right"><NumCell rowKey={rowKey} col={col} /></td>
      ))}
      <td className="py-1.5 px-3 text-right font-mono text-sm font-semibold tabular-nums text-slate-900 bg-slate-50/60"
          data-testid={`lt-${rowKey}-trimestre`}>{fmt(rowTri(rowKey))}</td>
      <td className="py-1 px-2">
        {editable ? (
          <input
            type="text"
            className="w-full bg-transparent text-xs italic text-slate-500 px-1 py-1 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:bg-white"
            value={filas?.[rowKey]?.obs ?? ''}
            onChange={(e) => onCellChange?.(rowKey, 'obs', e.target.value)}
            data-testid={`lt-${rowKey}-obs`}
            placeholder="—"
          />
        ) : (
          <span className="text-xs italic text-slate-400">{filas?.[rowKey]?.obs || ''}</span>
        )}
      </td>
    </tr>
  );

  const CompRow = ({ label, vals, testid, highlight = false, strong = false }) => (
    <tr className={cn('border-b border-slate-200', highlight ? 'bg-slate-100' : 'bg-slate-50/70')} data-testid={testid}>
      <td className={cn('py-2 px-3 text-sm sticky left-0', highlight ? 'bg-slate-100 font-bold text-slate-900' : 'bg-slate-50/70 font-semibold text-slate-800')}>{label}</td>
      {COLS.map((m, idx) => (
        <td key={m} className={cn('py-2 px-2 text-right font-mono text-sm tabular-nums', vals[m] < 0 ? 'text-rose-700' : 'text-slate-800', strong && 'font-semibold')}>
          {colInactive[idx] ? <span className="text-slate-300">—</span> : fmt(vals[m])}
        </td>
      ))}
      <td className={cn('py-2 px-3 text-right font-mono text-sm tabular-nums font-bold', vals.trimestre < 0 ? 'text-rose-700' : 'text-emerald-800', highlight && 'text-base')}
          data-testid={`${testid}-trimestre`}>
        {fmt(vals.trimestre)}
      </td>
      <td className="py-2 px-2" />
    </tr>
  );

  const SectionRow = ({ label }) => (
    <tr className="bg-emerald-50" data-testid={`lt-section-${label}`}>
      <td className="py-1.5 px-3 text-xs font-bold uppercase tracking-wider text-emerald-800 sticky left-0 bg-emerald-50">{label}</td>
      <td className="bg-emerald-50" /><td className="bg-emerald-50" /><td className="bg-emerald-50" /><td className="bg-emerald-50" /><td className="bg-emerald-50" />
    </tr>
  );

  return (
    <div className="overflow-auto rounded-lg border border-slate-200" data-testid="lt-table">
      <table className="w-full min-w-[720px] border-collapse">
        <thead>
          <tr className="bg-[#2f5e4e] text-white">
            <th className="text-left text-xs font-semibold uppercase tracking-wider py-2.5 px-3 sticky left-0 bg-[#2f5e4e] w-[26%]">Concepto</th>
            <th className="text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-2 w-[14%]">Mes 1</th>
            <th className="text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-2 w-[14%]">Mes 2</th>
            <th className="text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-2 w-[14%]">Mes 3</th>
            <th className="text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-3 w-[14%]">Trimestre</th>
            <th className="text-left text-xs font-semibold uppercase tracking-wider py-2.5 px-3 w-[18%]">Observaciones</th>
          </tr>
        </thead>
        <tbody>
          {/* Mes / período */}
          <tr className="border-b border-slate-200 bg-white">
            <td className="py-1.5 px-3 text-sm font-semibold text-slate-800 sticky left-0 bg-white">Mes / período</td>
            {COLS.map((m, idx) => (
              <td key={m} className="py-1 px-2 text-right">
                {colInactive[idx] ? (
                  <span className="text-xs text-slate-300">—</span>
                ) : editable ? (
                  <input
                    type="text"
                    className="w-full bg-transparent text-right text-xs text-slate-600 px-1 py-1 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:bg-white"
                    value={labelsSafe[idx] ?? ''}
                    onChange={(e) => onMesLabelChange?.(idx, e.target.value)}
                    data-testid={`lt-mes-label-${idx}`}
                  />
                ) : (
                  <span className="text-xs text-slate-500">{labelsSafe[idx]}</span>
                )}
              </td>
            ))}
            <td className="bg-slate-50/60" />
            <td />
          </tr>

          <SectionRow label="INGRESOS" />
          <EditRow rowKey="ingresos_membresias" />

          <SectionRow label="EGRESOS OPERATIVOS" />
          {LT_EGRESO_KEYS.map((k) => <EditRow key={k} rowKey={k} />)}

          <CompRow label="Total egresos operativos" vals={c.totalEg} testid="lt-total-egresos" strong />
          <CompRow label="Resultado antes de fee" vals={c.resAntes} testid="lt-resultado-antes-fee" strong />
          <EditRow rowKey="gastos_extraordinarios" />
          <CompRow label={`Fee de gestión GCSAS (${feePct}% s/ mes positivo)`} vals={c.fee} testid="lt-fee" />
          <EditRow rowKey="recupero_expensas" />
          <CompRow label="RESULTADO NETO A DISTRIBUIR" vals={c.neto} testid="lt-resultado-neto" highlight strong />
        </tbody>
      </table>
    </div>
  );
};

export default LiquidacionTrimestralTable;
